/**
 * EN-1848: where closing a drawer manager panel (Report Manager, Download
 * Manager, Broadcast Commands, Stream Manager) takes the user.
 *
 * The panels share the left drawer with the device list and the Pig Run
 * Manager and only one shows at a time. Closing a panel returns to the page
 * the user came from: the Pig Run Manager when the panel was opened from it
 * (its Report button, or the main menu while it was showing), otherwise the
 * device / map page.
 */

export type ManagerPanel =
  | 'pigRunManager'
  | 'reportManager'
  | 'downloadManager'
  | 'broadcastCommands'
  | 'streamManager';

/** What closing a panel returns to. */
export type PanelReturnTarget = 'device' | 'pigRunManager';

export interface ManagerPanelState {
  showPigRunManager: boolean;
  showDeviceInfo: boolean;
  showDeviceTable: boolean;
  showReportManager: boolean;
  showDownloadManager: boolean;
  showBroadcastCommands: boolean;
  showStreamManager: boolean;
  managerReturnTo: PanelReturnTarget;
}

const SUB_PANEL_FLAGS = [
  'showReportManager',
  'showDownloadManager',
  'showBroadcastCommands',
  'showStreamManager',
] as const;

type PanelFlag =
  | 'showPigRunManager'
  | 'showReportManager'
  | 'showDownloadManager'
  | 'showBroadcastCommands'
  | 'showStreamManager';

const PANEL_FLAG: Record<ManagerPanel, PanelFlag> = {
  pigRunManager: 'showPigRunManager',
  reportManager: 'showReportManager',
  downloadManager: 'showDownloadManager',
  broadcastCommands: 'showBroadcastCommands',
  streamManager: 'showStreamManager',
};

/** Shows the device list and map side panels, hiding every manager panel. */
export function showDevicePanels(state: ManagerPanelState): void {
  state.showPigRunManager = false;
  state.showDeviceInfo = true;
  state.showDeviceTable = true;
  state.showReportManager = false;
  state.showDownloadManager = false;
  state.showBroadcastCommands = false;
  state.showStreamManager = false;
  state.managerReturnTo = 'device';
}

/**
 * Opens a manager panel, remembering the page it was opened from: the Pig Run
 * Manager if that was showing, the page the user first came from if another
 * panel was showing (so switching panels from the main menu never loops
 * between them), otherwise the device page.
 */
export function openManagerPanel(
  state: ManagerPanelState,
  panel: ManagerPanel
): void {
  let returnTo: PanelReturnTarget = 'device';
  if (panel !== 'pigRunManager') {
    if (state.showPigRunManager) {
      returnTo = 'pigRunManager';
    } else if (SUB_PANEL_FLAGS.some((flag) => state[flag])) {
      returnTo = state.managerReturnTo;
    }
  }
  state.showPigRunManager = false;
  state.showDeviceInfo = false;
  state.showDeviceTable = false;
  state.showReportManager = false;
  state.showDownloadManager = false;
  state.showBroadcastCommands = false;
  state.showStreamManager = false;
  state[PANEL_FLAG[panel]] = true;
  state.managerReturnTo = returnTo;
}

/**
 * Closes the open Report Manager, Download Manager, Broadcast Commands or
 * Stream Manager and returns to the page it was opened from. Returns that page,
 * or null (and changes nothing) when none of those panels is showing.
 */
export function closeManagerPanel(
  state: ManagerPanelState
): PanelReturnTarget | null {
  if (!SUB_PANEL_FLAGS.some((flag) => state[flag])) return null;
  if (state.managerReturnTo === 'pigRunManager') {
    openManagerPanel(state, 'pigRunManager');
    return 'pigRunManager';
  }
  showDevicePanels(state);
  return 'device';
}
